import React from 'react';
import { Link, useParams } from 'react-router-dom';
import PageSEO from '../../components/PageSEO';
import SiteHeader from '../../components/SiteHeader';
import SiteFooter from '../../components/SiteFooter';
import { getPostBySlug, getPublishedPosts } from '../../content/blog';

const GenericBlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <div className="min-h-screen bg-aspen-warm-ivory dark:bg-aspen-night-sage">
        <SiteHeader />
        <div className="max-w-3xl mx-auto py-12 px-4 text-center">
          <h1 className="text-2xl font-bold text-aspen-deep-eucalyptus dark:text-aspen-pearl">
            Post not found
          </h1>
          <Link to="/blog" className="text-aspen-vibrant-blue hover:underline mt-4 inline-block">
            ← Back to Blog
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  // Get related posts (same category, different slug, max 3)
  const related = getPublishedPosts()
    .filter((p) => p.category === post.category && p.slug !== post.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-aspen-warm-ivory dark:bg-aspen-night-sage">
      <PageSEO
        title={post.title}
        description={post.description}
        canonicalPath={`/blog/${post.slug}`}
        breadcrumbName={post.title}
        breadcrumbParent={{ name: 'Blog', path: '/blog' }}
        ogType="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.description,
          author: { '@type': 'Organization', name: 'Ask Aspen' },
          publisher: {
            '@type': 'Organization',
            name: 'Ask Aspen',
            logo: { '@type': 'ImageObject', url: 'https://www.askaspen.app/aspen-logomark.webp' },
          },
          datePublished: post.publishDate,
          dateModified: post.publishDate,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.askaspen.app/blog/${post.slug}`,
          },
        }}
      />
      <SiteHeader />

      <article className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/blog" className="text-aspen-vibrant-blue hover:underline text-sm font-medium">
              ← Back to Blog
            </Link>
          </div>
          <h1 className="text-4xl font-bold text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-aspen-sage-teal dark:text-aspen-gentle-teal">
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
        </header>

        <div
          className="prose prose-lg max-w-none text-aspen-deep-eucalyptus dark:text-aspen-pearl
            prose-headings:text-aspen-deep-eucalyptus dark:prose-headings:text-aspen-pearl
            prose-a:text-aspen-vibrant-blue prose-strong:text-aspen-deep-eucalyptus
            dark:prose-strong:text-aspen-pearl"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {related.length > 0 && (
          <footer className="mt-12 pt-8 border-t border-aspen-blush-clay/30 dark:border-aspen-moonlit-clay/30">
            <h3 className="text-lg font-semibold text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-4">
              Related
            </h3>
            <ul className="space-y-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link to={`/blog/${r.slug}`} className="text-aspen-vibrant-blue hover:underline">
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </footer>
        )}
      </article>

      <SiteFooter />
    </div>
  );
};

export default GenericBlogPost;
